/* Clever Boutique — cards, quick add, product page, collection toolbar.
   Add-to-cart goes through Horizon's CartLinesUpdateEvent so the theme's
   cart drawer and cart bubble update exactly as with the native form. */

let Events = null;
try {
  Events = await import('@shopify/events');
} catch (e) {
  console.warn('[clever] @shopify/events unavailable, falling back to /cart', e);
}

const root = window.Shopify?.routes?.root || '/';
const $ = (s, el = document) => el.querySelector(s);
const $$ = (s, el = document) => Array.from(el.querySelectorAll(s));
const readJSON = (el) => {
  try { return JSON.parse(el?.textContent || '[]'); } catch { return []; }
};

/* ---------------- cart ---------------- */
async function addToCart(id, quantity = 1, source = document.body) {
  const sectionIds = $$('cart-items-component')
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  if (!Events) {
    const r = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity }] }),
    });
    if (!r.ok) throw new Error((await r.json()).description || 'Could not add to cart');
    window.location.href = `${root}cart`;
    return;
  }

  const { CartLinesUpdateEvent } = Events;
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(id), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity }], sections: sectionIds.join(',') }),
    });
    const data = await res.json();
    const cart = await (await fetch(`${root}cart.js`)).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'clever',
        itemCount: quantity,
        sections: data.sections,
        didError: Boolean(data.status),
      },
    });
    if (data.status) throw new Error(data.description || data.message || 'Could not add to cart');
    return cart;
  } catch (err) {
    deferred.reject(err);
    throw err;
  }
}

/* ---------------- cards ---------------- */
function initCard(card) {
  if (card.dataset.cbReady) return;
  card.dataset.cbReady = '1';
  const variants = readJSON($('[data-cb-variants]', card));
  const hasSizes = card.dataset.hasSizes === 'true';
  const img = $('[data-cb-card-img]', card);
  const hover = $('[data-cb-card-hover]', card);
  const links = $$('[data-cb-card-link]', card);
  const qa = $('[data-cb-qa]', card);
  const addBtn = $('[data-cb-quick-add]', card);

  const forColour = (c) => variants.filter((v) => v.c === c);

  card.setColour = (colour) => {
    const list = forColour(colour);
    if (!list.length) return;
    card.dataset.colour = colour;
    const v = list.find((x) => x.a) || list[0];
    if (img && v.img) {
      img.removeAttribute('srcset');
      img.src = v.img;
      if (hover) hover.style.display = 'none';
    }
    links.forEach((a) => (a.href = `${card.dataset.url}?variant=${v.id}`));
    $$('[data-cb-swatch]', card).forEach((b) =>
      b.setAttribute('aria-pressed', String(b.dataset.cbSwatch === colour))
    );
    if (qa) qa.hidden = true;
  };

  $$('[data-cb-swatch]', card).forEach((b) =>
    b.addEventListener('click', () => card.setColour(b.dataset.cbSwatch))
  );

  const flash = () => {
    addBtn.classList.add('is-done');
    setTimeout(() => addBtn.classList.remove('is-done'), 1400);
  };

  const add = async (v, el) => {
    el.classList.add('is-loading');
    try {
      await addToCart(v.id, 1, card);
      if (qa) qa.hidden = true;
      flash();
    } catch (e) {
      alertInline(qa || card, e.message);
    } finally {
      el.classList.remove('is-loading');
    }
  };

  addBtn?.addEventListener('click', () => {
    const list = forColour(card.dataset.colour);
    if (!hasSizes) {
      const v = list.find((x) => x.a);
      if (v) add(v, addBtn);
      return;
    }
    if (!qa) return;
    if (!qa.hidden) { qa.hidden = true; return; }
    qa.innerHTML = '';
    const head = document.createElement('div');
    head.className = 'cb-qa__head';
    head.innerHTML = `<span>اختاري المقاس · ${card.dataset.colour}</span>`;
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'cb-qa__close';
    close.setAttribute('aria-label', 'Close');
    close.textContent = '×';
    close.onclick = () => (qa.hidden = true);
    head.append(close);
    const sizes = document.createElement('div');
    sizes.className = 'cb-qa__sizes';
    list.forEach((v) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cb-pill' + (v.a ? '' : ' is-unavailable');
      b.textContent = v.s;
      b.disabled = !v.a;
      b.onclick = () => add(v, b);
      sizes.append(b);
    });
    qa.append(head, sizes);
    qa.hidden = false;
    sizes.querySelector('button:not([disabled])')?.focus();
  });
}

function alertInline(host, msg) {
  let p = host.querySelector('.cb-inline-err');
  if (!p) {
    p = document.createElement('p');
    p.className = 'cb-inline-err cb-pdp__error';
    p.setAttribute('role', 'alert');
    host.append(p);
  }
  p.textContent = msg;
  host.hidden = false;
}

/* ---------------- product page ---------------- */
function initPDP(pdp) {
  if (pdp.dataset.cbReady) return;
  pdp.dataset.cbReady = '1';
  const variants = readJSON($('[data-cb-pdp-variants]', pdp));
  const form = $('[data-cb-pdp-form]', pdp);
  const idInput = $('[data-cb-variant-id]', pdp);
  const addBtn = $('[data-cb-add]', pdp);
  const addLabel = $('[data-cb-add-label]', pdp);
  const buyNow = $('[data-cb-buy-now]', pdp);
  const err = $('[data-cb-error]', pdp);
  const qtyInput = $('[data-cb-qty-input]', pdp);
  const optionEls = $$('[data-cb-option]', pdp);
  const thumbs = $$('[data-cb-thumb]', pdp);
  const slides = $$('[data-cb-slide]', pdp);
  const stage = $('[data-cb-stage]', pdp);
  const dots = $('[data-cb-dots]', pdp);
  const priceEl = $('[data-cb-price]', pdp);
  const mobile = window.matchMedia('(max-width: 989px)');

  let selected = optionEls.map((o) => $('[aria-pressed="true"]', o)?.dataset.cbValue);
  let current = variants.find((v) => v.id === Number(idInput.value)) || variants[0];

  const find = (opts) => variants.find((v) => v.options.every((o, i) => o === opts[i]));

  const playActive = () => {
    slides.forEach((s) => {
      const v = s.querySelector('video');
      if (!v) return;
      const on = mobile.matches ? isInView(s) : s.classList.contains('is-active');
      if (on) v.play().catch(() => {});
      else v.pause();
    });
  };
  const isInView = (s) => {
    const r = s.getBoundingClientRect(), sr = stage.getBoundingClientRect();
    return Math.abs(r.left - sr.left) < sr.width / 2 && !s.hidden;
  };

  const showSlide = (mediaId, scroll = true) => {
    slides.forEach((s) => s.classList.toggle('is-active', s.dataset.cbSlide === String(mediaId)));
    requestAnimationFrame(playActive);
    thumbs.forEach((t) => t.setAttribute('aria-current', String(t.dataset.cbThumb === String(mediaId))));
    if (mobile.matches && scroll) {
      const s = slides.find((x) => x.dataset.cbSlide === String(mediaId));
      if (s) stage.scrollTo({ left: s.offsetLeft, behavior: 'instant' });
    }
  };

  const filterGallery = (colour) => {
    const has = slides.some((s) => s.dataset.colour === colour);
    const keep = (el) => !has || el.dataset.colour === colour || el.dataset.colour === 'all';
    slides.forEach((s) => (s.hidden = !keep(s)));
    thumbs.forEach((t) => (t.hidden = !keep(t)));
    buildDots();
  };

  const buildDots = () => {
    if (!dots) return;
    const visible = slides.filter((s) => !s.hidden);
    dots.innerHTML = visible.length > 1 ? visible.map(() => '<span></span>').join('') : '';
    syncDots();
  };
  const syncDots = () => {
    if (!dots || !mobile.matches) return;
    const visible = slides.filter((s) => !s.hidden);
    const i = Math.round(stage.scrollLeft / (stage.clientWidth || 1));
    $$('span', dots).forEach((d, n) => d.classList.toggle('is-active', n === i));
  };
  stage?.addEventListener('scroll', () => requestAnimationFrame(() => { syncDots(); playActive(); }), { passive: true });

  const updateAvailability = () => {
    optionEls.forEach((o, i) => {
      $$('[data-cb-value]', o).forEach((b) => {
        const opts = [...selected];
        opts[i] = b.dataset.cbValue;
        const v = find(opts);
        b.classList.toggle('is-unavailable', !v || !v.a);
      });
    });
  };

  const render = () => {
    optionEls.forEach((o, i) => {
      $$('[data-cb-value]', o).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.cbValue === selected[i])));
      const label = $('[data-cb-opt-value]', o);
      if (label) label.textContent = selected[i];
    });
    current = find(selected);
    updateAvailability();
    err.hidden = true;
    if (!current) {
      addBtn.disabled = buyNow.disabled = true;
      addLabel.textContent = 'Unavailable';
      return;
    }
    idInput.value = current.id;
    addBtn.disabled = buyNow.disabled = !current.a;
    addLabel.textContent = current.a ? 'Add to cart' : 'Sold out';
    if (priceEl) {
      $('.cb-price__now', priceEl).textContent = `EGP ${current.price}`;
      let was = $('.cb-price__was', priceEl);
      if (current.compare) {
        if (!was) { was = document.createElement('s'); was.className = 'cb-price__was'; priceEl.append(was); }
        was.textContent = `EGP ${current.compare}`;
      } else was?.remove();
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', current.id);
    history.replaceState({}, '', url);
  };

  optionEls.forEach((o, i) => {
    $$('[data-cb-value]', o).forEach((b) =>
      b.addEventListener('click', () => {
        selected[i] = b.dataset.cbValue;
        if (!find(selected)?.a && i === 0) {
          // keep a purchasable size when switching colour
          const alt = variants.find((v) => v.options[0] === selected[0] && v.a);
          if (alt) selected = [...alt.options];
        }
        render();
        if (i === 0) {
          filterGallery(selected[0]);
          const v = variants.find((x) => x.options[0] === selected[0] && x.media);
          const first = slides.find((s) => !s.hidden);
          showSlide(v?.media || first?.dataset.cbSlide);
        }
      })
    );
  });

  thumbs.forEach((t) => t.addEventListener('click', () => showSlide(t.dataset.cbThumb)));

  $$('[data-cb-qty]', pdp).forEach((b) =>
    b.addEventListener('click', () => {
      qtyInput.value = Math.max(1, (Number(qtyInput.value) || 1) + Number(b.dataset.cbQty));
    })
  );

  $('[data-cb-open-size]', pdp)?.addEventListener('click', () => {
    const d = $('[data-cb-size-acc]', pdp);
    if (!d) return;
    d.open = true;
    d.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });

  const fail = (e) => {
    err.textContent = e.message || 'Something went wrong. Please try again.';
    err.hidden = false;
  };

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!current?.a) return;
    addBtn.classList.add('is-loading');
    try {
      await addToCart(current.id, Math.max(1, Number(qtyInput.value) || 1), form);
    } catch (x) { fail(x); } finally { addBtn.classList.remove('is-loading'); }
  });

  buyNow.addEventListener('click', async () => {
    if (!current?.a) return;
    buyNow.classList.add('is-loading');
    try {
      const r = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: current.id, quantity: Math.max(1, Number(qtyInput.value) || 1) }] }),
      });
      const d = await r.json();
      if (d.status) throw new Error(d.description || d.message);
      window.location.href = `${root}checkout`;
    } catch (x) { fail(x); buyNow.classList.remove('is-loading'); }
  });

  // initial state
  updateAvailability();
  filterGallery(selected[0]);
  showSlide(current?.media || slides.find((s) => !s.hidden)?.dataset.cbSlide, false);
}

/* ---------------- collection ---------------- */
function initCollection(coll) {
  if (coll.dataset.cbReady) return;
  coll.dataset.cbReady = '1';
  const grid = $('[data-cb-coll-grid]', coll);
  const cards = $$('[data-cb-card]', grid);
  const count = $('[data-cb-count]', coll);
  const empty = $('[data-cb-empty]', coll);
  const total = count?.textContent;
  let active = null;

  $$('[data-cb-chip]', coll).forEach((chip) =>
    chip.addEventListener('click', () => {
      active = active === chip.dataset.cbChip ? null : chip.dataset.cbChip;
      $$('[data-cb-chip]', coll).forEach((c) => c.setAttribute('aria-pressed', String(c.dataset.cbChip === active)));
      let shown = 0;
      cards.forEach((card) => {
        const colours = readJSON($('[data-cb-variants]', card)).map((v) => v.c);
        const ok = !active || colours.includes(active);
        card.hidden = !ok;
        if (ok) {
          shown++;
          if (active) card.setColour?.(active);
        }
      });
      if (count) count.textContent = active ? `${shown} ${shown === 1 ? 'product' : 'products'}` : total;
      if (empty) empty.hidden = shown > 0;
    })
  );

  $('[data-cb-sort]', coll)?.addEventListener('change', (e) => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', e.target.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  $$('[data-cb-cols]', coll).forEach((b) =>
    b.addEventListener('click', () => {
      grid.style.setProperty('--cols', b.dataset.cbCols);
      $$('[data-cb-cols]', coll).forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    })
  );
}

/* ---------------- boot ---------------- */
function boot(scope = document) {
  $$('[data-cb-card]', scope).forEach(initCard);
  $$('[data-cb-pdp]', scope).forEach(initPDP);
  $$('[data-cb-coll]', scope).forEach(initCollection);
}
boot();
document.addEventListener('shopify:section:load', (e) => boot(e.target));
